import { computeCameraSnapshotForBounds } from '@/core/cameraFit'
import { hydrateMeta, initialMeta } from '@/document/hydration'
import type { SnapshotMeta } from '@/document/model'
import { createDocumentBase } from '@/storage/grid/documentBase'
import type { Position } from '@/voxelMath'
import type { VoxelData, VoxelMap } from '@/voxelTypes'

export type PageDocumentOptions = {
  /** The page id (PageDefinition.id) and its format version. */
  page: { id: string; version: number }
  bounds: Position
  palette: VoxelData[]
  backgroundColor: number
  /**
   * Camera polar angle (0 = straight down), azimuth and zoom, fitted to the bounds; `target` the point
   * looked at (default the bounds centre). `fitHeight`: frame the world as if it were only this tall,
   * for a tall world whose height is room to build into (025's stacking), not something to show.
   * `fit: 'footprint'`: frame the floor's rectangle to the screen (its width across, its depth up,
   * `zoom` the share of the screen it fills) instead of the bounds' sphere, for a flat world in the
   * screen's shape (`canvasFootprint`); assumes `theta` 0 (looking up the z axis). `inset`: px along
   * the top and bottom edges kept for the page's bars; the rectangle is fitted to and centred in the
   * screen between them (the target moves). `fit: 'wall'`: the same for a world that stands up, a
   * wall seen nearly face on (`phi` near pi/2): its width across and its height (`bounds.y`) up.
   */
  camera?: {
    phi?: number
    theta?: number
    zoom?: number
    margin?: number
    target?: Position
    fitHeight?: number
    fit?: 'sphere' | 'footprint' | 'wall'
    inset?: { top?: number; bottom?: number }
    /** The lens (vertical field of view, degrees; default 45). A narrow one sees a flat sheet with little convergence. */
    fov?: number
    /**
     * With `fit: 'footprint'`: a frame in screen px instead of `zoom`'s share. The sheet is placed
     * in the free rectangle (between the insets, or the whole screen with `bars: false`) with at
     * least `side` px at the sides and `vertical` px above and below, centred: composed for the
     * screen in hand (src/play/environment/scenes.ts, Mat).
     */
    frame?: { side: number; vertical: number; bars?: boolean; /** Cover the rectangle instead of fitting in it (the sheet runs off the other direction's edges). */ cover?: boolean }
  }
  /** Sun position; defaults to high and slightly behind. */
  light?: Position
  /** Grids and the bounds border; off by default (pages draw their own world). */
  grids?: boolean
  /** Extra meta fields (e.g. `mirrors`). */
  meta?: Partial<SnapshotMeta>
}

export type PageCameraOptions = NonNullable<PageDocumentOptions['camera']>

/**
 * The camera a page frames `bounds` with (`PageDocumentOptions.camera`): the fit every new page
 * document gets, and what a scene recomputes live when the owner changes the view
 * (src/play/environment/scenes.ts). `fov` default 45.
 */
export function pageCameraSnapshot(bounds: Position, cam: PageCameraOptions = {}) {
  const fov = cam.fov ?? FOV
  const fitBounds = cam.fitHeight ? { ...bounds, y: Math.min(bounds.y, cam.fitHeight) } : bounds
  const phi = cam.phi ?? 0.8
  const margin = cam.margin ?? 0.92
  const footprint = cam.fit === 'footprint' ? footprintFrame(fitBounds, phi, margin, cam.zoom ?? 0.92, cam.inset ?? {}, fov, cam.frame)
    : cam.fit === 'wall' ? wallFrame(fitBounds, phi, margin, cam.zoom ?? 0.92, cam.inset ?? {}, fov)
    : null
  const zoom = footprint?.zoom ?? cam.zoom ?? 0.92
  const target = cam.target ?? footprint?.target
  return computeCameraSnapshotForBounds(fitBounds, {
    fov,
    phi,
    theta: cam.theta ?? 0.35,
    margin,
    zoom,
    cameraMode: 'orbit',
    ...(target ? { target } : {}),
  })
}

/** A page document from cells and a few look settings (the boilerplate every page shares). */
export function createPageDocument(entries: Iterable<[number, VoxelData]>, opts: PageDocumentOptions): { base: VoxelMap; meta: SnapshotMeta } {
  const { bounds } = opts
  const base = createDocumentBase(Array.from(entries), undefined, bounds)
  const grids = opts.grids ?? false
  const meta = hydrateMeta(
    {
      ...initialMeta(),
      bounds,
      palette: opts.palette.map((item) => ({ ...item })),
      backgroundColor: opts.backgroundColor,
      camera: pageCameraSnapshot(bounds, opts.camera),
      lighting: { directional: { position: opts.light ?? { x: bounds.x * 0.2, y: bounds.y * 2.2 + 40, z: -bounds.z * 0.4 } } },
      ...(grids ? {} : {
        boundsAppearance: {
          baseGrid: { mode: 'off' },
          wallGrid: { mode: 'off' },
          border: { visible: false },
        },
      }),
      ...opts.meta,
      experience: { id: opts.page.id, version: opts.page.version },
    } as SnapshotMeta,
    'lenient',
  )
  return { base, meta }
}

const FOV = 45

/** The screen, in px (a default outside a browser). */
function screenPx(): { w: number; h: number } {
  const w = typeof window !== 'undefined' && window.innerWidth > 0 ? window.innerWidth : 1280
  const h = typeof window !== 'undefined' && window.innerHeight > 0 ? window.innerHeight : 800
  return { w, h }
}

/** What the orbit fit gives at zoom 1 (shared/camera-fit.mjs): the bounds' sphere in the narrower angle. */
function orbitSphere(bounds: Position, fov: number, margin: number, w: number, h: number): number {
  const vFov = (fov * Math.PI) / 180
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * Math.max(0.1, w / h))
  const halfDiag = 0.5 * Math.hypot(bounds.x, bounds.y, bounds.z)
  return (halfDiag / Math.sin(Math.max(0.2, Math.min(vFov, hFov) / 2))) * margin
}

/**
 * The orbit fit's `zoom` (and target) that frames the floor rectangle of `bounds`, seen from polar
 * angle `phi` at azimuth 0, so it fills `fill` of the screen between the insets in its tighter
 * direction, its projected middle centred between them; or, with `frame`, so it keeps the frame's
 * px margins (the whole screen with `bars: false`; `cover`: fills it, running off the other
 * direction's edges). Perspective-correct: the near edge is the
 * widest, and the near half of a tipped sheet stands taller on screen than the far half, so the
 * distance and the target are found by iteration on the projected edges (a few passes; the fit
 * is exact at straight down and within a pixel when tipped). Also every footprint page's own
 * camera, through `createPageDocument`.
 */
function footprintFrame(
  bounds: Position,
  phi: number,
  margin: number,
  fill: number,
  inset: { top?: number; bottom?: number },
  fov: number,
  frame?: { side: number; vertical: number; bars?: boolean; cover?: boolean },
): { zoom: number; target: Position } {
  const { w, h } = screenPx()
  const between = frame && frame.bars === false ? {} : inset
  const top = between.top ?? 0
  const bottom = between.bottom ?? 0
  const free = Math.max(h * 0.4, h - top - bottom)
  const tanV = Math.tan(((fov * Math.PI) / 180) / 2)
  const tanH = tanV * Math.max(0.1, w / h)
  const tanFree = tanV * (free / h)
  const fillW = frame ? Math.max(0.2, 1 - (2 * frame.side) / w) : fill
  const fillH = frame ? Math.max(0.2, 1 - (2 * frame.vertical) / free) : fill
  const halfW = bounds.x / 2
  const halfD = bounds.z / 2
  const sin = Math.sin(phi)
  const cos = Math.cos(phi)
  // A point `s` along the sheet's depth from the target (+ toward the viewer), on screen (tan units, up +).
  const up = (s: number, d: number) => (-s * cos) / (d - s * sin)
  // The free rectangle's centre, up from the screen's (tan units): lower when the top inset is the larger.
  const centre = (((bottom - top) / 2) * 2 * tanV) / h
  let d = Math.max(halfW / (fillW * tanH), (halfD * cos) / (fillH * tanFree)) + halfD * sin
  let shift = 0
  for (let i = 0; i < 12; i++) {
    const far = -halfD - shift
    const near = halfD - shift
    const height = up(far, d) - up(near, d)
    const width = halfW / (d - near * sin)
    const byH = height / (2 * fillH * tanFree)
    const byW = width / (fillW * tanH)
    d *= frame?.cover ? Math.min(byH, byW) : Math.max(byH, byW)
    const mid = (up(far, d) + up(near, d)) / 2
    shift += ((centre - mid) * d) / cos
  }
  const target = { x: bounds.x / 2, y: bounds.y / 2, z: bounds.z / 2 + shift }
  return { zoom: d / orbitSphere(bounds, fov, margin, w, h), target }
}

/**
 * As `footprintFrame` for a world that stands up (a wall, `fit: 'wall'`): its width across and its
 * height (`bounds.y`) up, seen at `pi/2 - phi` off its normal.
 */
function wallFrame(bounds: Position, phi: number, margin: number, fill: number, inset: { top?: number; bottom?: number }, fov: number): { zoom: number; target: Position } {
  const tilt = Math.PI / 2 - phi
  const { w, h } = screenPx()
  const top = inset.top ?? 0
  const bottom = inset.bottom ?? 0
  const free = Math.max(h * 0.4, h - top - bottom)
  const vFov = (fov * Math.PI) / 180
  const tanV = Math.tan(vFov / 2)
  const tanH = tanV * Math.max(0.1, w / h)
  const tanFree = tanV * (free / h)
  const halfW = bounds.x / 2 / fill
  const halfD = bounds.y / 2 / fill
  const distance = Math.max(halfW / tanH, (halfD * Math.cos(tilt)) / tanFree) + halfD * Math.abs(Math.sin(tilt))
  const unitsPerPx = (2 * distance * tanV) / h
  const shift = (((top - bottom) / 2) * unitsPerPx) / Math.cos(tilt)
  const target = { x: bounds.x / 2, y: bounds.y / 2 + shift, z: bounds.z / 2 }
  return { zoom: distance / orbitSphere(bounds, fov, margin, w, h), target }
}
